/**
 * Supabase Authentication Provider
 * 
 * Implements the IAuthProvider interface using Supabase Auth.
 * This adapter translates Supabase-specific types to our generic auth types,
 * allowing the rest of the application to be provider-agnostic.
 */

import type { SupabaseClient, User, Session } from '@supabase/supabase-js';
import type { IAuthProvider } from '../interfaces/IAuthProvider';
import type {
  AuthResult,
  AuthSession,
  AuthUser,
  AuthStateChangeCallback,
  AuthSubscription,
} from '../interfaces/types';
import { AuthError } from '../interfaces/types';

export class SupabaseAuthProvider implements IAuthProvider {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Convert Supabase User to our AuthUser type
   */
  private mapUser(user: User | null): AuthUser | null {
    if (!user) return null;

    return {
      id: user.id,
      email: user.email || '',
      email_confirmed_at: user.email_confirmed_at,
      created_at: user.created_at,
      updated_at: user.updated_at,
      user_metadata: user.user_metadata,
    };
  }

  /**
   * Convert Supabase Session to our AuthSession type
   */
  private mapSession(session: Session | null): AuthSession | null {
    if (!session) return null;

    return {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at || 0,
      expires_in: session.expires_in || 0,
      token_type: session.token_type,
      user: this.mapUser(session.user)!,
    };
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }

    return {
      user: this.mapUser(data.user),
      session: this.mapSession(data.session),
    };
  }

  async signUp(
    email: string,
    password: string,
    metadata?: Record<string, any>
  ): Promise<AuthResult> {
    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }

    return {
      user: this.mapUser(data.user),
      session: this.mapSession(data.session),
    };
  }

  async signOut(): Promise<void> {
    console.log('[SupabaseAuthProvider] signOut called');
    const { error } = await this.supabase.auth.signOut();
    console.log('[SupabaseAuthProvider] Supabase signOut completed, error:', error);
    if (error) {
      console.error('[SupabaseAuthProvider] SignOut error:', error);
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }
    console.log('[SupabaseAuthProvider] signOut successful');
  }

  async getSession(): Promise<AuthSession | null> {
    const { data, error } = await this.supabase.auth.getSession();
    if (error) {
      // Only throw on unexpected errors, not "no session" errors
      if (error.status !== 401 && !error.message.includes('session missing')) {
        throw new AuthError(error.message, error.status?.toString(), error.status);
      }
      return null;
    }
    return this.mapSession(data.session);
  }

  async getUser(): Promise<AuthUser | null> {
    const { data, error } = await this.supabase.auth.getUser();
    if (error) {
      // Only throw on unexpected errors, not "no session" errors
      if (error.status !== 401 && !error.message.includes('session missing')) {
        throw new AuthError(error.message, error.status?.toString(), error.status);
      }
      return null;
    }
    return this.mapUser(data.user);
  }

  async refreshSession(): Promise<AuthSession | null> {
    const { data, error } = await this.supabase.auth.refreshSession();
    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }
    return this.mapSession(data.session);
  }

  onAuthStateChange(callback: AuthStateChangeCallback): AuthSubscription {
    const { data } = this.supabase.auth.onAuthStateChange((event, session) => {
      callback(event as any, this.mapSession(session));
    });

    return {
      unsubscribe: () => data.subscription.unsubscribe(),
    };
  }

  async resetPassword(email: string, redirectTo?: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo || `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }
  }

  async updateUser(attributes: {
    email?: string;
    password?: string;
    data?: Record<string, any>;
  }): Promise<AuthUser> {
    const { data, error } = await this.supabase.auth.updateUser(attributes);

    if (error) {
      throw new AuthError(error.message, error.status?.toString(), error.status);
    }

    if (!data.user) {
      throw new AuthError('User update failed - no user returned');
    }

    return this.mapUser(data.user)!;
  }
}

